import React, { useState, useEffect, useLayoutEffect, useRef, Fragment } from 'react';
import { makeStyles } from 'tss-react/mui';
import { Container, Box, Typography } from '@mui/material';
import NewsfeedCard from './NewsfeedCard/NewsfeedCard';
import NewsfeedCardCBB from './NewsfeedCard/NewsfeedCardCBB';
import SelectedNewsfeedCheerUpBanner from './SelectedNewsfeedCheerUpBanner';
import { CampaignCodeControl } from '../../../components/CampaignCodeControl/CampaignCodeControl';
import { newsAnchorId } from './newsAnchor';

const useStyles = makeStyles()(theme => ({
  wrapper: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    overflowX: 'hidden'
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.125rem',
    color: '#617c89'
  },
  noData: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.125rem',
    color: '#617c89'
  },
  contentWrapper: {
    flex: 1
  },
  footer: {
    marginTop: '2rem',
    paddingBottom: '1rem'
  },
  cheerUpBannerWrapper: {
    margin: '2rem 16px'
  },
  newsAnchor: {
    scrollMarginTop: 'calc(45px + env(safe-area-inset-top, 0px) + 8px)'
  }
}));

const host = process.env.HEADLESS_CMS_URL || '';

const SelectedNewsfeedDetailView = props => {
  const { intl } = props;
  const { classes } = useStyles();
  const [newsfeed, setNewsfeed] = useState(props?.location?.state?.news || props?.newsfeed);
  const [loading, setLoading] = useState(true);
  const { id } = props.match.params;
  const contentRef = useRef(null);

  useEffect(() => {
    if (newsfeed) {
      setLoading(false);
      return;
    }
    setLoading(true);

    const apiUrl = `${host}/api/selected-newsfeeds/${id}?sort=createdAt:desc&pagination[limit]=1&populate[news][populate]=*`;
    fetch(apiUrl)
      .then(res => res.json())
      .then(data => {
        if (data?.data) {
          setNewsfeed(data.data);
        }
      })
      .catch(err => console.error('Error fetching newsfeed:', err))
      .finally(() => setLoading(false));
  }, [id, newsfeed]);

  const hash = (props.location?.hash || '').replace(/^#/, '');

  useLayoutEffect(() => {
    if (loading || !hash.startsWith('news-')) {
      return undefined;
    }

    const anchor = decodeURIComponent(hash);
    let cancelled = false;

    const align = () => {
      if (cancelled) {
        return;
      }
      const el = document.getElementById(anchor);
      if (!el) {
        return;
      }
      if (Math.abs(el.getBoundingClientRect().top - 53) < 40) {
        return;
      }
      el.scrollIntoView({ behavior: 'auto', block: 'start' });
    };

    align();
    const onImageLoad = () => align();
    const images = contentRef.current ? Array.from(contentRef.current.querySelectorAll('img')) : [];
    images.forEach(img => {
      if (!img.complete) {
        img.addEventListener('load', onImageLoad);
      }
    });
    const correction = window.setTimeout(align, 400);
    const stopListening = window.setTimeout(() => {
      images.forEach(img => img.removeEventListener('load', onImageLoad));
    }, 2500);

    return () => {
      cancelled = true;
      window.clearTimeout(correction);
      window.clearTimeout(stopListening);
      images.forEach(img => img.removeEventListener('load', onImageLoad));
    };
  }, [loading, hash, newsfeed]);

  if (loading) {
    return (
      <Box className={classes.loading}>
        <Typography>{intl.formatMessage({ id: 'selected-newsfeed-loading' })}</Typography>
      </Box>
    );
  }

  if (!newsfeed || !newsfeed.attributes.news || newsfeed.attributes.news.length === 0) {
    return (
      <Box className={classes.noData}>
        <Typography>{intl.formatMessage({ id: 'selected-newsfeed-no-newsfeed-found' })}</Typography>
      </Box>
    );
  }

  const cheerUp = newsfeed.attributes.cheer_up;
  const news = newsfeed.attributes.news;
  // console.log('Props : ', props)

  return (
    <Fragment key={props.location.pathname}>
      <Container
        maxWidth="md"
        style={{
          height: 'auto',
          padding: '46px 0px 80px 0px'
        }}
      >
        <Box className={classes.wrapper}>
          <Box className={classes.cheerUpBannerWrapper}>
            <SelectedNewsfeedCheerUpBanner message={cheerUp} />
          </Box>
          <Box className={classes.contentWrapper} ref={contentRef}>
            {news.map((newsItem, index) => (
              <Box
                key={newsItem.id || newsItem.news_id || index}
                id={newsAnchorId(newsItem, index)}
                className={classes.newsAnchor}
              >
                <CampaignCodeControl
                  targetCampaignCode={['ENTCBB']}
                  userCampaignCodes={props.userDetails?.campaignCodes}
                  teamCampaignCodes={props.teamCampaignCodes}
                  fallbackComponent={
                    <NewsfeedCard key={index} news={newsItem} intl={intl} history={props.history} newsfeedId={id} />
                  }
                >
                  <NewsfeedCardCBB key={index} news={newsItem} intl={intl} history={props.history} newsfeedId={id} />
                </CampaignCodeControl>
              </Box>
            ))}
          </Box>
        </Box>
      </Container>
    </Fragment>
  );
};

export default SelectedNewsfeedDetailView;
